import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { auditTime } from 'rxjs/operators';
import { TimerEntryService } from '@intranet/modules/timer/timer-entry.service';

/**
 * Type of cross-window sync event.
 * - timer-changed:   running-timer state changed (start/stop/job/description/start-time). Broadcast over BroadcastChannel.
 * - entries-changed: time entries list changed (create/edit/delete). Broadcast over BroadcastChannel.
 * - poll-tick:       periodic local tick used as a cross-process bridge for changes that
 *                    BroadcastChannel can't reach (e.g. browser ↔ Electron). Local-only.
 *                    Subscribers should reconcile lightweight state (running timer, entries)
 *                    on this event but NOT do expensive work like reloading dropdown caches.
 */
export type TimerSyncEventType =
    | 'timer-changed'
    | 'entries-changed'
    | 'poll-tick'
    /** A long-running-timer alert was dismissed somewhere — sibling windows
     *  in the same browser should clear their copy too. Cross-process
     *  delivery (browser tab ↔ desktop app) goes through the server's
     *  dismissal tracker instead and arrives on the next poll-tick. */
    | 'long-timer-dismissed';

export interface TimerSyncEvent {
    type: TimerSyncEventType;
    /** Timestamp of the broadcast — useful for debouncing rapid bursts. */
    t: number;
}

/**
 * Cross-window timer sync via BroadcastChannel.
 *
 * Same-origin windows (e.g. installed PWA + browser tab) can each subscribe to
 * `event$` and refresh their data when an event arrives. Same-machine only —
 * cross-device sync requires a separate polling/push layer (planned).
 *
 * Auto-broadcasts on `TimerEntryService` events for the running-timer.
 * Entry-list mutations (edit/delete/create) call `notify('entries-changed')`
 * explicitly from the components that perform the mutation.
 */
@Injectable({ providedIn: 'root' })
export class TimerSyncService implements OnDestroy {
    private static readonly CHANNEL_NAME = 'visarc-timer-sync';
    /** Window with a running suppression is applying a remote event — don't echo it back. */
    private static readonly SUPPRESS_WINDOW_MS = 1500;

    private channel: BroadcastChannel | null = null;
    private _event$ = new Subject<TimerSyncEvent>();
    private _suppressUntil = 0;
    private subs: Subscription[] = [];
    private initialised = false;
    private pollingHandle: ReturnType<typeof setInterval> | null = null;

    /** Emits when another window broadcasts a sync event. Auto-debounced (rapid bursts coalesced). */
    readonly event$: Observable<TimerSyncEvent> = this._event$.asObservable().pipe(auditTime(150));

    constructor(private timerEntryService: TimerEntryService) {}

    init(): void {
        if (this.initialised) return;
        this.initialised = true;

        if (typeof BroadcastChannel === 'undefined') {
            // Old browser — nothing to do. event$ will simply never emit.
            return;
        }

        this.channel = new BroadcastChannel(TimerSyncService.CHANNEL_NAME);
        this.channel.addEventListener('message', (e: MessageEvent<TimerSyncEvent>) => {
            // Suppress local re-broadcasts triggered by applying this remote event.
            this._suppressUntil = Date.now() + TimerSyncService.SUPPRESS_WINDOW_MS;
            this._event$.next(e.data);
        });

        // Auto-broadcast on running-timer state changes.
        const broadcastTimerChanged = () => this.broadcast('timer-changed');
        this.subs.push(this.timerEntryService.newEntry$.subscribe(broadcastTimerChanged));
        this.subs.push(this.timerEntryService.stopEntry$.subscribe(broadcastTimerChanged));
        this.subs.push(this.timerEntryService.updateDescription$.subscribe(broadcastTimerChanged));
        this.subs.push(this.timerEntryService.updateLiveJob$.subscribe(broadcastTimerChanged));
        this.subs.push(this.timerEntryService.updateStartTime$.subscribe(broadcastTimerChanged));
        // Long-timer alert dismissal — separate type so subscribers can
        // mirror the dismissal locally without forcing a full timer-state
        // refetch from the backend.
        this.subs.push(
            this.timerEntryService.longTimerDismissed$.subscribe(
                () => this.broadcast('long-timer-dismissed'),
            ),
        );
    }

    /** Manually broadcast — used for entry-list mutations that don't go through TimerEntryService. */
    notify(type: TimerSyncEventType): void {
        this.broadcast(type);
    }

    /**
     * Begin emitting a `poll-tick` event locally every `intervalMs`. Used to bridge
     * cross-process sync gaps where BroadcastChannel can't reach — e.g. between
     * the user's normal browser and an Electron window, which run in separate
     * Chromium processes. Subscribers (timer-header, timer-tracker) reconcile
     * lightweight state on each tick and pick up changes made elsewhere.
     *
     * `poll-tick` is intentionally distinct from real BroadcastChannel events
     * so subscribers can skip expensive work (e.g. reloading dropdown caches)
     * that only matters on actual user-driven mutations.
     *
     * Local-only — does NOT broadcast to other windows. Each window polls
     * independently. Idempotent; calling again resets the interval.
     */
    startPolling(intervalMs: number): void {
        this.stopPolling();
        this.pollingHandle = setInterval(() => {
            this._event$.next({ type: 'poll-tick', t: Date.now() });
        }, intervalMs);
    }

    stopPolling(): void {
        if (this.pollingHandle !== null) {
            clearInterval(this.pollingHandle);
            this.pollingHandle = null;
        }
    }

    private broadcast(type: TimerSyncEventType): void {
        if (!this.channel) return;
        if (Date.now() < this._suppressUntil) return; // we're applying a remote event — don't loop
        this.channel.postMessage({ type, t: Date.now() } as TimerSyncEvent);
    }

    ngOnDestroy(): void {
        this.subs.forEach(s => s.unsubscribe());
        this.channel?.close();
        this.stopPolling();
    }
}
